import React, { useMemo, useState } from "react";
import styles from "./TravelAgencyProfile.module.css";
import { useNavigate } from "react-router-dom";
import { AlertBox, Button, Text, Icon, IconButton, Input, SectionHeader } from "@zorni/components-lib";
import { useAddConversation } from "../../event/messenger/hooks/useAddConversation";
import { useZorni } from "../../../hooks/useZorni";
import { useOperation } from "../../../../dashboard/hooks/useOperation";

const TravelAgencyProfile = ({ tripPublicationId, tripReference, establishmentId, embedded = false }) => {
  const { userId } = useZorni();
  const navigate = useNavigate();

  const { addConversationWithMessage, loading: sendingConv } = useAddConversation();
  const { showToast } = useOperation();

  // 🔹 Zone de saisie du premier message, affichée seulement après clic sur le CTA
  const [isComposing, setIsComposing] = useState(false);
  const [message, setMessage] = useState("");
  const [validationError, setValidationError] = useState(null);

  const defaultData = {
    name: "Découvertes du Monde",
    type: "Agence Premium",
    image: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=400",
    stats: { totalTrips: 247, rating: 4.92, experience: 12 },
    specialties:
      "Voyages culturels en Europe, circuits en Asie, découvertes en Amérique du Sud",
    formula:
      "Circuits accompagnés, séjours sur mesure, voyages en petits groupes (8-15 personnes)",
    strengths:
      "Guides francophones experts, hébergements soigneusement sélectionnés, itinéraires authentiques",
    services: "Assistance 24h/24, assurance voyage incluse, transferts aéroport",
    team:
      "Notre équipe vous accompagne de la réservation jusqu'à votre retour pour garantir un voyage inoubliable",
    info: {
      satisfaction: "98%",
      responseTime: "Sous 2 heures",
      license: "IM075180042",
      guarantee: "APST",
    },
  };

  const data = defaultData;

  const metaRows = useMemo(
    () => [
      { label: "Taux de satisfaction", value: data.info.satisfaction },
      { label: "Délai de réponse", value: data.info.responseTime },
      { label: "Licence agréée", value: data.info.license },
      { label: "Garantie financière", value: data.info.guarantee },
    ],
    [data]
  );

  // 🔹 Étape 1 : ouvrir la zone de saisie (ne crée encore rien)
  const openCompose = () => {
    setIsComposing(true);
    setValidationError(null);
  };

  // 🔹 Annulation : ferme la zone, vide le texte, aucune conversation créée
  const cancelCompose = () => {
    if (sendingConv) return; // évite d'annuler pendant une création/envoi en cours
    setIsComposing(false);
    setMessage("");
    setValidationError(null);
  };

  // 🔹 Étape 2 : création de la conversation + envoi du premier message
  const handleSendConversationMessage = async () => {
    if (sendingConv) return; // anti double-clic

    const trimmed = message.trim();
    if (!trimmed) {
      setValidationError("Merci de saisir un message avant l'envoi.");
      return;
    }

    setValidationError(null);

    try {
      const conversation = await addConversationWithMessage(
        {
          tripPublicationId,
          tripReference,
          establishmentId,
          customerId: userId,
        },
        trimmed
      );

      // Redirection uniquement si conversation + premier message ont tous deux réussi
      navigate(`/messenger/${conversation.id}`);
    } catch (err) {
      console.error("Erreur lors de la création de la conversation :", err);
      showToast(
        err?.message || "Une erreur est survenue. Merci de réessayer.",
        "error"
      );
    }
  };

  return (
    <section className={styles.section}>
      {/* embedded : dans une colonne de page (détail du voyage), sans largeur ni marges propres */}
      <div className={embedded ? styles.containerEmbedded : styles.container}>
        <SectionHeader
          className={styles.header}
          title="Faites connaissance avec votre agence"
          subtitle="Informations, garanties, et un canal direct pour poser vos questions avant de réserver."
        />

        <div className={styles.grid}>
          {/* Colonne gauche : profil */}
          <div className={styles.left}>
            <div className={styles.profileCard}>
              <div className={styles.profileTop}>
                <div className={styles.avatarWrap}>
                  <img
                    src={data.image}
                    alt={data.name}
                    className={styles.avatar}
                    onError={(e) => {
                      e.currentTarget.src = "https://via.placeholder.com/400?text=Agence";
                    }}
                  />
                  <span className={styles.verified} title="Vérifié">
                    ✓
                  </span>
                </div>

                <div className={styles.profileIdentity}>
                  <Text size="lg" weight="bold" className={styles.agencyName}>
                    {data.name}
                  </Text>

                  <div className={styles.badges}>
                    <span className={styles.badge}>
                      <span className={styles.badgeDot} />
                      <Text size="xs" weight="bold">
                        {data.type}
                      </Text>
                    </span>

                    <span className={styles.badgeSoft}>
                      <Text size="xs" weight="bold">
                        Note {data.stats.rating} / 5
                      </Text>
                    </span>
                  </div>
                </div>
              </div>

              <div className={styles.statsRow}>
                <div className={styles.stat}>
                  <Text size="lg" weight="bold">{data.stats.totalTrips}</Text>
                  <Text size="xs" className={styles.muted}>voyages organisés</Text>
                </div>
                <div className={styles.stat}>
                  <Text size="lg" weight="bold">{data.stats.rating}</Text>
                  <Text size="xs" className={styles.muted}>note globale</Text>
                </div>
                <div className={styles.stat}>
                  <Text size="lg" weight="bold">{data.stats.experience}</Text>
                  <Text size="xs" className={styles.muted}>années d’expérience</Text>
                </div>
              </div>

              <div className={styles.blocks}>
                <div className={styles.block}>
                  <Text size="sm" weight="bold">Spécialités</Text>
                  <Text size="sm" className={styles.blockText}>{data.specialties}</Text>
                </div>

                <div className={styles.block}>
                  <Text size="sm" weight="bold">Formules</Text>
                  <Text size="sm" className={styles.blockText}>{data.formula}</Text>
                </div>

                <div className={styles.block}>
                  <Text size="sm" weight="bold">Points forts</Text>
                  <Text size="sm" className={styles.blockText}>{data.strengths}</Text>
                </div>

                <div className={styles.block}>
                  <Text size="sm" weight="bold">Services</Text>
                  <Text size="sm" className={styles.blockText}>{data.services}</Text>
                </div>

                <div className={styles.blockSoft}>
                  <Text size="sm">{data.team}</Text>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne droite : infos + CTA (sticky sur desktop) */}
          <aside className={styles.right}>
            <div className={styles.sticky}>
              <div className={styles.contactCard}>
                <Text size="lg" weight="bold">
                  Contacter l’agence
                </Text>
                <Text size="sm" className={styles.muted}>
                  Posez vos questions, demandez un devis, ou discutez des détails avant de réserver.
                </Text>

                <div className={styles.meta}>
                  {metaRows.map((row) => (
                    <div key={row.label} className={styles.metaRow}>
                      <Text size="sm" className={styles.metaLabel}>{row.label}</Text>
                      <Text size="sm" weight="bold" className={styles.metaValue}>{row.value}</Text>
                    </div>
                  ))}
                </div>

                <div className={styles.cta}>
                  {!isComposing ? (
                    <Button
                      variant="primary"
                      label="Envoyer un message / Demander un devis"
                      onClick={openCompose}
                    />
                  ) : (
                    <div className={styles.composeBox}>
                      <Input
                        multiline
                        rows={4}
                        placeholder="Décrivez votre demande (dates souhaitées, nombre de participants...)"
                        value={message}
                        onChange={(e) => {
                          setMessage(e.target.value);
                          if (validationError) setValidationError(null);
                        }}
                        disabled={sendingConv}
                        errors={validationError}
                      />

                      <div className={styles.composeActions}>
                        {sendingConv && (
                          <Text size="xs" className={styles.composeHint}>
                            Envoi en cours...
                          </Text>
                        )}

                        <IconButton
                          iconName="iconClose"
                          aria-label="Annuler"
                          onClick={cancelCompose}
                          className={`${styles.composeCancel} ${sendingConv ? styles.composeDisabled : ""}`}
                        />
                        <IconButton
                          iconName="iconSend"
                          aria-label="Envoyer le message"
                          onClick={handleSendConversationMessage}
                          className={`${styles.composeSend} ${sendingConv ? styles.composeDisabled : ""}`}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.disclaimer}>
                  <div className={styles.discIcon}>
                    <Icon name="iconCompliance" size="md" />
                  </div>
                  <Text size="xs" className={styles.discText}>
                    Pour votre sécurité, effectuez toujours vos paiements via la plateforme.
                    N’envoyez jamais d’argent en dehors de Zorni.
                  </Text>
                </div>

                <button
                  type="button"
                  className={styles.linkBtn}
                  onClick={() => {
                    // plus tard: ouvrir une page "En savoir plus"
                  }}
                >
                  <Text size="sm" weight="bold">En savoir plus sur les garanties</Text>
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default TravelAgencyProfile;
